import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import UDCard from 'ud-ui/components/card';
import UDPerPage from 'ud-ui/components/per-page';
import UDSearchInput from 'ud-ui/components/search';

export type ToolbarSearchMode = 'input' | 'placeholder' | 'hidden';

/**
 * Поверхность панели:
 * - `plain` — без своей подложки: список внутри карточки (вкладка команды, игрока), карточка вокруг уже есть;
 * - `card` — панель на своей белой карточке: список-страница, где поиск, фильтры и «На странице» иначе
 *   лежат россыпью прямо на сером фоне.
 */
export type ToolbarSurface = 'plain' | 'card';

/**
 * Как лежат поиск и `extraFilters` — выбирает страница, а не наличие фильтров:
 * - `inline` — по умолчанию: поле поиска до 320 px, фильтры сразу за ним в той же строке, без подписи у поиска.
 *   Так живёт всё, что кладёт в `extraFilters` не подписанные поля, а чипы и свои группы (вкладка «Заявки»);
 * - `grid` — поиск и фильтры — ячейки одной сетки равной ширины, у поиска подпись «Поиск:», как у соседей.
 *   Для страницы, где каждый фильтр — подписанное поле (организаторы): иначе верх поиска оказывается ниже.
 */
export type ToolbarFiltersLayout = 'inline' | 'grid';

export interface UDPagedListToolbarProps {
  /** Что набрано в поле прямо сейчас (черновик, до дебаунса). */
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Ввод ещё не доехал до запроса: лупа сменяется спиннером. */
  isSearchLoading?: boolean;
  /**
   * Что стоит на месте поля поиска. Выбирает `UDEntityList`, см. `getSearchMode`:
   * - `input` — само поле;
   * - `placeholder` — заглушка ровно его размера, пока первая загрузка не сказала, есть ли что искать;
   * - `hidden` — ничего: список пуст сам по себе. Фильтры, действия и «На странице» остаются.
   */
  searchMode?: ToolbarSearchMode;
  /** Поверхность панели. По умолчанию `plain`; список-страницу (`UDPagedList`) ставит на `card`. */
  surface?: ToolbarSurface;
  /** Раскладка поиска и фильтров. По умолчанию `inline`; сетку включает страница, чьи фильтры — подписанные поля. */
  filtersLayout?: ToolbarFiltersLayout;
  /**
   * Размер страницы. Необязателен: список без пагинации (`UDEntityList`) его не
   * передаёт, и тогда «На странице» не рисуется вовсе.
   */
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
  /** Дополнительные фильтры страницы — год, спорт, сортировка. */
  extraFilters?: React.ReactNode;
  /** Кнопки действий над списком — «Добавить …», «Создать …». Рисуются справа. */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * Верхняя панель списка: поиск, фильтры страницы, действия, размер страницы.
 *
 * Индикатор загрузки живёт **в поле поиска**, а не поверх выдачи: так видно,
 * что запрос вызван вводом, и список при этом остаётся на экране.
 *
 * Слева — то, что сужает выдачу (поиск, фильтры), справа — то, что её меняет или
 * настраивает (кнопки, размер страницы). Поэтому `actions` и `extraFilters` не
 * взаимозаменяемы: кнопка «Добавить игрока» рядом с фильтрами читается как фильтр.
 */
const UDPagedListToolbar = ({
  search,
  onSearchChange,
  searchPlaceholder,
  isSearchLoading,
  searchMode = 'input',
  surface = 'plain',
  filtersLayout = 'inline',
  perPage,
  onPerPageChange,
  extraFilters,
  actions,
  className,
}: UDPagedListToolbarProps) => {
  const { t } = useLingui();
  const handleChange = useCallback(
    (event: React.FocusEvent<HTMLInputElement, Element>) => onSearchChange(event.target.value),
    [onSearchChange],
  );

  const hasPerPage = perPage !== undefined && onPerPageChange !== undefined;

  // Без поля и без всего остального панель была бы пустым блоком с нижним отступом.
  if (searchMode === 'hidden' && !extraFilters && !actions && !hasPerPage) return null;

  // Сетке нужны фильтры, которые в неё лягут: без них она была бы пустой рамкой вокруг одного поля.
  const isGrid = filtersLayout === 'grid' && Boolean(extraFilters);

  // В сетке у поиска, как у соседей, есть подпись: иначе его верх оказывался ниже подписанных полей.
  const searchInput = (
    <UDSearchInput
      value={search}
      label={isGrid ? t`Поиск:` : undefined}
      placeholder={searchPlaceholder}
      loading={isSearchLoading}
      onChange={handleChange}
      width="100%"
    />
  );

  const searchField =
    searchMode === 'input' ? (
      searchInput
    ) : searchMode === 'placeholder' ? (
      // Заглушка — то же поле, только невидимое (`invisible` держит место и убирает его из
      // фокуса и из дерева доступности): размер совпадает сам, без подобранных пикселей,
      // и когда поле появится на её месте, соседи не сдвинутся.
      <div className="skeleton-block" aria-hidden="true" data-testid="paged-list-search-placeholder">
        <div className="invisible">{searchInput}</div>
      </div>
    ) : null;

  const trailing = (actions || hasPerPage) && (
    <div className="ml-auto flex flex-wrap items-end gap-4 self-end">
      {actions}
      {hasPerPage && <UDPerPage value={perPage} onChange={onPerPageChange} />}
    </div>
  );

  // Один набор классов на обе раскладки: ветки различаются только содержимым.
  const rowClassName = classNames(
    'flex flex-wrap items-end gap-y-3',
    isGrid ? 'gap-x-6' : 'gap-x-4',
    surface === 'plain' && 'mb-4',
    surface === 'plain' && className,
  );

  const row = isGrid ? (
    // Сетка: поиск и фильтры — равные колонки, которые сами переносятся по ширине (от 11.5 rem на колонку),
    // а колонки в строках выровнены друг под другом. «На странице» и действия — справа, под сеткой, если
    // рядом не помещаются.
    <div className={rowClassName}>
      <div
        className="grid min-w-0 flex-[1_1_40rem] grid-cols-[repeat(auto-fit,minmax(min(100%,11.5rem),1fr))] items-end gap-x-4 gap-y-3 *:min-w-0"
        data-testid="paged-list-toolbar-grid"
      >
        {searchField && <div>{searchField}</div>}
        {extraFilters}
      </div>
      {trailing}
    </div>
  ) : (
    // Строка: поле до 320 px, фильтры сразу за ним, справа действия и «На странице».
    <div className={rowClassName}>
      {searchField && <div className="max-w-80 min-w-56 flex-[1_1_20rem]">{searchField}</div>}
      {extraFilters}
      {trailing}
    </div>
  );

  if (surface === 'plain') return row;

  return (
    <UDCard padding="sm" className={classNames('mb-4', className)} data-testid="paged-list-toolbar-card">
      {row}
    </UDCard>
  );
};

export default UDPagedListToolbar;
